import { attr, FASTElement, Notifier, nullableNumberConverter, observable, Observable, Updates, volatile } from '@microsoft/fast-element';
import dayjs from 'dayjs';
import IMask, { InputMask, MaskedOptions } from "imask";
import { jsonConverter } from '../../value-converters/json-value-converter';
import { textFieldStyles } from './text-field.styles';
import { textFieldTemplate } from './text-field.template';
import { FormAssociated } from '../../foundation/form-associated';
import { NatGenDesignSystem } from '../../natgen-design-system';
import { TextFieldType } from './text-field.options';
import { ARIAGlobalStatesAndProperties } from '../../foundation/aria-global';
import { applyMixins } from '../../foundation/apply-mixins';
import { StartEnd } from '../start-end/start-end';
import { CSSClassBehavior } from '../../decorators/class-list-decorator';
import { HostAttributeStripper } from '../../foundation/host-attribute-stripper';

export type NatGenLabelAppearance = 'default' | 'material';
export type NatGenTextFieldAppearance = 'outline' | 'stealth';

function typeChangedHandler(source: NatGenTextField, propertyName: string) {
    if (propertyName === 'type' && source.type === TextFieldType.password && !source.isPasswordType)
        source.isPasswordType = true;
}

class _TextField extends FASTElement {}
interface _TextField extends FormAssociated {}

/**
 * A form-associated base class for the {@link @microsoft/fast-foundation#(TextField:class)} component.
 *
 * @beta
 */
export class FormAssociatedTextField extends FormAssociated(_TextField) {
    proxy = document.createElement("input");
}

class StrippableFormAssociatedTextField extends HostAttributeStripper(FormAssociatedTextField) {}

/**
 * @element natgen-text-field
 */
export class NatGenTextField extends StrippableFormAssociatedTextField {
    public static readonly hostAttributesToStrip = ["aria-label", "aria-labelledby"];

    public internalId: string = crypto.randomUUID();

    @observable
    public controlAriaLabel: string | null = null;

    @observable
    public controlAriaLabelledby: string | null = null;

    private associatedLabelObserver?: MutationObserver;
    private observedAssociatedLabels: Element[] = [];
    private readonly boundHandleTextInput = (e?: Event) => this.handleTextInput(e);
    private readonly boundHandleChange = () => this.handleChange();
    private readonly boundHandleFocus = () => this.handleFocus();
    private readonly boundHandleBlur = () => this.handleBlur();

    /**
     * The appearance of the element.
     *
     * @public
     * @remarks
     * HTML Attribute: appearance
     */
    @attr
    public appearance: NatGenTextFieldAppearance;

    public appearanceChanged(prev: NatGenTextFieldAppearance, next: NatGenTextFieldAppearance) {
        this.classList.remove(prev);
        this.classList.add(next);
    }

    /**
     * The appearance the anchor should have.
     *
     * @public
     * @remarks
     * HTML Attribute: labelAppearance
     */
    @attr
    public labelAppearance: NatGenLabelAppearance;

    /**
     * The maximum value a user can enter.
     * @public
     * @remarks
     * HTMLAttribute: max
     */
    @attr
    public max: string;
    private maxChanged(): void {
        if (this.proxy instanceof HTMLElement) {
            this.proxy.max = this.max;
            this.validate();
        }
    }

    /**
     * The minimum value a user can enter.
     * @public
     * @remarks
     * HTMLAttribute: min
     */
    @attr
    public min: string;
    private minChanged(): void {
        if (this.proxy instanceof HTMLElement) {
            this.proxy.min = this.min;
            this.validate();
        }
    }

    @attr({ attribute: "disable-smart-punctuation", mode: "boolean" })
    public disableSmartPunctuation: boolean = false;
    private disableSmartPunctuationChanged() {
        if (this.disableSmartPunctuation && this.control?.value)
            this.control.value = this.cleanSmartPunctuation(this.control.value);
    }

    private cleanSmartPunctuation(value: string): string {
        return value.replace(/[\u2018\u2019]/g, "'") // single-quote variants
            .replace(/[\u201C\u201D]/g, '"') // double-quite variants
            .replace(/[\u2014]/g, '-'); // dash variants
    }

    @attr({ attribute: "force-uppercase", mode: "boolean" })
    public forceUppercase: boolean = false;
    private forceUppercaseChanged() {
        if (this.forceUppercase && this.control?.value)
            this.control.value = this.forceTextToUppercase(this.control.value);
    }

    private forceTextToUppercase(value: string): string {
        const upper = value.toUpperCase();
        return upper;
    }

    public get hasLabel() : boolean {
        return this.defaultSlottedNodes && this.defaultSlottedNodes.some(n => n.textContent.trim().length > 0);
    }

    protected maskInstance: InputMask<any>;

    @attr
    public mask: string;

    @attr({ converter: jsonConverter, attribute: 'mask-settings' })
    public maskSettings: MaskedOptions;

    /**
     * Unmask field contents on input.
     */
    @attr({ mode: 'boolean', attribute: 'auto-unsecure' })
    public autoUnsecure: boolean = false;

    /**
     * Remask field contents after auto-unsecure has unmasked and the field is blurred.
     */
    @attr({ mode: 'boolean', attribute: 'auto-resecure' })
    public autoResecure: boolean = false;

    /**
     * Masking is only applied when the field is focused. When the field is blurred, the mask is removed and the unmasked value is displayed.
     */
    @attr({ mode: 'boolean', attribute: 'focus-mask' })
    public focusMask: boolean = false;

    protected maskChangedHandler(source: NatGenTextField, propertyName: string) {
        if (propertyName == 'mask' || propertyName == 'maskSettings')
            source.updateMask();
    }

    protected updateMask(): void {
        if (!this.control)
            return;

        this.control.removeEventListener('input', this.boundHandleTextInput);
        this.control.removeEventListener('change', this.boundHandleChange);

        if ((this.mask || this.maskSettings) && (!this.focusMask || this.hasFocus)) {
            if (this.maskInstance)
                this.maskInstance.destroy();

            var maskSettings: any = this.maskSettings || { mask: this.mask };

            // TODO: Mask helpers should be broken out of text-field so they can be used in other components and for raw text. #JoshD
            if (this.mask?.toLowerCase() == 'number')
                maskSettings.mask = IMask.MaskedNumber;
            else if (this.mask?.toLowerCase() == 'range')
                maskSettings.mask = IMask.MaskedRange;
            else if (this.mask?.toLowerCase() == 'date') {
                maskSettings.mask = Date;
                maskSettings.pattern = maskSettings.pattern || 'MM/DD/YYYY'
                maskSettings.blocks = {
                    DD: {
                        mask: IMask.MaskedRange,
                        from: 1,
                        to: 31,
                        maxLength: 2,
                    },
                    MM: {
                        mask: IMask.MaskedRange,
                        from: 1,
                        to: 12,
                        maxLength: 2,
                    },
                    YYYY: {
                        mask: IMask.MaskedRange,
                        from: 1900,
                        to: 9999,
                    }
                };

                maskSettings.format = (date: Date) => {
                    return dayjs(date).format(maskSettings.pattern);
                };

                maskSettings.parse = function (str) {
                    return dayjs(str, maskSettings.pattern);
                };
            } else if (this.mask?.toLowerCase() == 'us-phone')
                maskSettings.mask = '(000) 000-0000';
            else if (this.mask?.toLocaleLowerCase() == "credit-card")
            {
                // TODO: Credit Card mask with icons? #JoshD
            }
            else if (this.mask?.toLowerCase() == 'zip-code')
                maskSettings.mask = '00000';
            else if (this.mask?.toLowerCase() == 'decimal') {
                maskSettings.mask = [
                    {mask: '\\0.\\00'},
                    {mask: '\\0.00'},
                    {mask: '0.00'},
                    {mask: '00.00'},
                    {mask: '000.00'},
                    {mask: '0,000.00'},
                    {mask: '00,000.00'},
                    {mask: '000,000.00'},
                    {mask: '0,000,000.00'},
                  ];
            }
            else if (this.mask)
                maskSettings.mask = this.mask;

            this.maskInstance = IMask(this.control, maskSettings);
            this.maskInstance.on('accept', this.boundHandleTextInput);
            this.maskInstance.on('complete', this.boundHandleChange);
        } else {
            this.maskInstance?.destroy();
            delete this.maskInstance;
            this.control.addEventListener('input', this.boundHandleTextInput);
            this.control.addEventListener('change', this.boundHandleChange);
        }

        this.valueChanged(this.value, this.value);
    }

    @observable isPasswordType = false;

    protected notifier: Notifier;

    togglePasswordVisibility() {
        if (this.type == TextFieldType.password)
            this.type = TextFieldType.text;
        else
            this.type = TextFieldType.password;
    }

    @attr({ attribute: 'auto-tab' })
    public autoTab: string;

    connectedCallback() {
        super.connectedCallback();

        this.$fastController.addBehavior(new CSSClassBehavior('readOnly', 'readonly'));
        this.$fastController.addBehavior(new CSSClassBehavior('hasLabel', 'label__visible', 'label__hidden'));

        this.addEventListener('focus', this.boundHandleFocus);
        this.addEventListener('blur', this.boundHandleBlur);

        this.proxy.setAttribute("type", this.type);
        this.validate();

        if (this.autofocus) {
            Updates.enqueue(() => {
                this.focus();
            });
        }

        if (!this.labelAppearance) {
            this.labelAppearance = "default";
        }

        this.isPasswordType = this.type === TextFieldType.password;

        this.notifier = Observable.getNotifier(this);
        this.notifier.subscribe({ handleChange: typeChangedHandler }, 'type');

        this.syncControlAccessibleName();
        this.updateMask();
        this.notifier.subscribe({ handleChange: this.maskChangedHandler }, 'mask');
        this.notifier.subscribe({ handleChange: this.maskChangedHandler }, 'maskSettings');
    }

    disconnectedCallback() {
        this.stopObservingAssociatedLabels();
        this.control?.removeEventListener('input', this.boundHandleTextInput);
        this.control?.removeEventListener('change', this.boundHandleChange);
        this.maskInstance?.destroy();
        delete this.maskInstance;
        this.notifier.unsubscribe({ handleChange: typeChangedHandler }, 'type');
        this.notifier.unsubscribe({ handleChange: this.maskChangedHandler }, 'mask');
        this.notifier.unsubscribe({ handleChange: this.maskChangedHandler }, 'maskSettings');
    }

    public ariaLabelChanged(): void {
        this.syncControlAccessibleName();
    }

    public ariaLabelledbyChanged(): void {
        this.syncControlAccessibleName();
    }

    valueChanged(previous: string, next: string) {
        super.valueChanged(previous, next);

        if (this.control) {
            this.control.value = next;

            if (this.maskInstance && this.mask?.toLowerCase() != 'decimal') {
                this.maskInstance.updateValue();
                this.maskInstance.updateControl('auto');
            }
        }
    }

    public handleTextInput(e = null): void {
        const start = Math.max(this.control.selectionStart, 0);
        const end = Math.max(this.control.selectionEnd, 0);

        if (this.mask?.toLowerCase() == 'decimal') {
            this.value = this.control.value.replaceAll(',', '');
            this.maskInstance.updateControl('auto');
            return;
        }

        if (this.disableSmartPunctuation) {
            this.control.value = this.cleanSmartPunctuation(this.control.value);
            this.control.setSelectionRange(start, end);
        }

        if (this.forceUppercase) {
            this.control.value = this.forceTextToUppercase(this.control.value);
        }

        if (this.maskInstance) {
            if (this.autoUnsecure && this.maskInstance.masked?.displayChar && e) {
                var displayChar = this.maskInstance.masked?.displayChar;

                this.maskInstance.updateOptions({ displayChar: null });
                
                if (this.autoResecure)
                    this.addEventListener('blur', () => { 
                        this.maskInstance.updateOptions({ displayChar: displayChar });
                        this.value = this.maskInstance.unmaskedValue;
                    }, { once: true });
            }

            this.value = this.maskInstance.unmaskedValue;
        } else
            this.value = this.control.value;
        
        // If the type is one of the selectable types, restore the selection range after updating the value
        if (['text', 'password', 'tel', 'url', 'search'].includes(this.type))
            this.control.setSelectionRange(start, end);

        if (this.autoTab && this.maxlength && this.value.length == this.maxlength) {
            (document.querySelector(this.autoTab) as HTMLElement)?.focus()
        }
    }

    /**
     * When true, the input will be immutable by user interaction.
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute} for more information.
     * @public
     * @remarks
     * HTML Attribute: readonly
     */
    @attr({ attribute: "readonly", mode: "boolean" })
    public readOnly: boolean;
    protected readOnlyChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.readOnly = this.readOnly;
            this.validate();
        }
    }

    /**
     * Indicates that this element should get focus after the page finishes loading.
     * See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefautofocus | autofocus HTML attribute}
     * for more information.
     * @public
     * @remarks
     * HTML Attribute: autofocus
     */
    @attr({ mode: "boolean" })
    public autofocus: boolean;
    protected autofocusChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.autofocus = this.autofocus;
            this.validate();
        }
    }

    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     * @public
     * @remarks
     * HTML Attribute: placeholder
     * Using this attribute does is not a valid substitute for a labeling element.
     */
    @attr
    public placeholder: string;
    protected placeholderChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.placeholder = this.placeholder;
        }
    }

    /**
     * Indicates whether the value of the control can be automatically completed by the browser.
     * @public
     * @remarks
     * HTML Attribute: placeholder
     */
    @attr
    public autocomplete: AutoFill;
    protected autocompleteChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.autocomplete = this.autocomplete;
        }
    }

    /**
     * Allows setting a type or mode of text.
     * @public
     * @remarks
     * HTML Attribute: type
     */
    @attr
    public type: TextFieldType = TextFieldType.text;
    private typeChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.type = this.type;
            if (this.proxy.value !== this.value) {
                this.value = this.proxy.value;
            }
            this.validate();
        }
    }

    /**
     * Allows associating a {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist | datalist}
     * to the element by {@link https://developer.mozilla.org/en-US/docs/Web/API/Element/id}.
     * @public
     * @remarks
     * HTML Attribute: list
     */
    @attr
    public list: string;
    protected listChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.setAttribute("list", this.list);
            this.validate();
        }
    }

    /**
     * The maximum number of characters a user can enter.
     * @public
     * @remarks
     * HTMLAttribute: maxlength
     */
    @attr({ converter: nullableNumberConverter })
    public maxlength: number;
    protected maxlengthChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.maxLength = this.maxlength;
            this.validate();
        }
    }

    /**
     * The minimum number of characters a user can enter.
     * @public
     * @remarks
     * HTMLAttribute: minlength
     */
    @attr({ converter: nullableNumberConverter })
    public minlength: number;
    protected minlengthChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.minLength = this.minlength;
            this.validate();
        }
    }

    /**
     * A regular expression that the value must match to pass validation.
     * @public
     * @remarks
     * HTMLAttribute: pattern
     */
    @attr
    public pattern: string;
    protected patternChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.pattern = this.pattern;
            this.validate();
        }
    }

    /**
     * Sets the width of the element to a specified number of characters.
     * @public
     * @remarks
     * HTMLAttribute: size
     */
    @attr({ converter: nullableNumberConverter })
    public size: number;
    protected sizeChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.size = this.size;
        }
    }

    /**
     * Controls whether or not to enable spell checking for the input field, or if the default spell checking configuration should be used.
     * @public
     * @remarks
     * HTMLAttribute: size
     */
    @attr({ mode: "boolean" })
    public spellcheck: boolean;
    protected spellcheckChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.spellcheck = this.spellcheck;
        }
    }

    /**
     * defines what action label (or icon) to present for the enter key on virtual keyboards.
     * @public
     * @remarks
     * HTMLAttribute: enterkeyhint
     */
    @attr({ attribute: "enterkeyhint" })
    public enterKeyHint:
        | "enter"
        | "done"
        | "go"
        | "next"
        | "previous"
        | "search"
        | "send";
    protected enterKeyHintChanged(): void {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.enterKeyHint = this.enterKeyHint;
        }
    }

    @observable
    public defaultSlottedNodes: Node[];
    public defaultSlottedNodesChanged() {
        Observable.notify(this, 'hasLabel');
        this.syncControlAccessibleName();
    }

    /**
     * A reference to the internal input field element
     * @internal
     */
    public control: HTMLInputElement;

    /**
     * Selects all the text in the text field
     *
     * @public
     */
    public select(): void {
        this.control.select();

        /**
         * The select event does not permeate the shadow DOM boundary.
         * This fn effectively proxies the select event,
         * emitting a `select` event whenever the internal
         * input emits a `select` event
         */
        this.$emit("select");
    }

    public hasFocus: boolean = false;

    public focus(options?: FocusOptions): void {        
        this.control?.focus(options);
    }

    public handleFocus() : void {
        if (this.focusMask) {
            this.hasFocus = true;
            this.updateMask();
        }
    }

    public handleBlur(): void {
        if (this.focusMask) {
            this.hasFocus = false;

            // Delay removing the mask until events can all flush
            Updates.enqueue(() => {
                this.updateMask();
            });
        }
    }

    /**
     * Change event handler for inner input.
     * @remarks
     * "Change" events are not `composable` so they will not
     * permeate the shadow DOM boundary. This fn effectively proxies
     * the change event, emitting a `change` event whenever the internal
     * input emits a `change` event
     * @internal
     */
    public handleChange(): void {
        this.$emit("change");
    }

    protected syncControlAccessibleName(): void {
        const explicitAriaLabel = this.getStrippedHostAttribute("aria-label") || this.ariaLabel?.trim();

        if (explicitAriaLabel) {
            this.stopObservingAssociatedLabels();
            this.controlAriaLabel = explicitAriaLabel;
            this.controlAriaLabelledby = null;
            return;
        }

        const associatedLabels = this.resolveAssociatedLabelElements();
        const derivedLabel = associatedLabels
            .map(label => label.textContent?.trim())
            .filter((label): label is string => !!label)
            .join(' ')
            .trim();

        this.observeAssociatedLabels(associatedLabels);

        if (derivedLabel) {
            this.controlAriaLabel = derivedLabel;
            this.controlAriaLabelledby = null;
            return;
        }

        this.stopObservingAssociatedLabels();
        this.controlAriaLabel = null;
        this.controlAriaLabelledby = this.getStrippedHostAttribute("aria-labelledby") || this.ariaLabelledby?.trim() || null;
    }

    protected resolveAssociatedLabelElements(): Element[] {
        const labelledByIds = (this.getStrippedHostAttribute("aria-labelledby") || this.ariaLabelledby)
            ?.split(/\s+/)
            .map(id => id.trim())
            .filter(Boolean) ?? [];

        if (!labelledByIds.length)
            return [];

        const searchRoots = [this.getRootNode(), document];
        const associatedLabels: Element[] = [];

        labelledByIds.forEach(id => {
            const label = searchRoots
                .map(root => root instanceof Document || root instanceof ShadowRoot || root instanceof Element
                    ? root.querySelector(`#${CSS.escape(id)}`)
                    : null)
                .find((candidate): candidate is Element => candidate instanceof Element);

            if (label && !associatedLabels.includes(label))
                associatedLabels.push(label);
        });

        return associatedLabels;
    }

    protected observeAssociatedLabels(labels: Element[]): void {
        const hasSameLabels = labels.length === this.observedAssociatedLabels.length
            && labels.every((label, index) => label === this.observedAssociatedLabels[index]);

        if (hasSameLabels)
            return;

        this.stopObservingAssociatedLabels();

        if (!labels.length)
            return;

        this.associatedLabelObserver = new MutationObserver(() => this.syncControlAccessibleName());
        labels.forEach(label => this.associatedLabelObserver?.observe(label, {
            characterData: true,
            childList: true,
            subtree: true,
        }));

        this.observedAssociatedLabels = labels;
    }

    protected stopObservingAssociatedLabels(): void {
        this.associatedLabelObserver?.disconnect();
        this.associatedLabelObserver = undefined;
        this.observedAssociatedLabels = [];
    }

    /** {@inheritDoc (FormAssociated:interface).validate} */
    public validate(): void {
        super.validate(this.control);
    }
}

/**
 * Includes ARIA states and properties relating to the ARIA textbox role
 *
 * @public
 */
export class DelegatesARIATextbox {}

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
/* eslint-disable-next-line */
export interface DelegatesARIATextbox extends ARIAGlobalStatesAndProperties {}
applyMixins(DelegatesARIATextbox, ARIAGlobalStatesAndProperties);

/**
 * Mark internal because exporting class and interface of the same name
 * confuses API documenter.
 * TODO: https://github.com/microsoft/fast/issues/3317
 * @internal
 */
export interface NatGenTextField extends StartEnd, DelegatesARIATextbox {}
applyMixins(NatGenTextField, StartEnd, DelegatesARIATextbox);

export const natGenTextFieldDefinition = NatGenTextField.compose({
    name: `${NatGenDesignSystem.prefix}-text-field`,
    template: textFieldTemplate(),
    styles: textFieldStyles,
    shadowOptions: {
        delegatesFocus: true,
    }
});
